import { useState } from "react";
import { Plus, Truck } from "lucide-react";
import { foApi } from "@/webapp/fleet-owners/api";
import RecordForm from "@/webapp/fleet-owners/tabs/RecordForm";
import VehicleDrawer from "@/webapp/fleet-owners/tabs/VehicleDrawer";
import { PageHead, Panel, Tag, EmptyState, Button, DataTable, Td, ExpiryCell } from "@/webapp/fleet-owners/ui";

export const VEHICLE_FIELDS = [
  { k: "unit", label: "Unit #", placeholder: "Unit 101" },
  { k: "plate", label: "Plate", placeholder: "ABC1234" },
  { k: "vin", label: "VIN", placeholder: "17 characters" },
  { k: "year", label: "Year", type: "number", placeholder: "2023" },
  { k: "make", label: "Make", placeholder: "Freightliner" },
  { k: "model", label: "Model", placeholder: "Cascadia" },
  { k: "vclass", label: "Class", placeholder: "Class 8 tractor" },
  { k: "trailer", label: "Trailer", placeholder: "53' dry van #T-22" },
  { k: "reg_expiry", label: "Registration expiry", type: "date" },
  { k: "insurance_expiry", label: "Insurance expiry", type: "date" },
  { k: "inspection_due", label: "Inspection due", type: "date" },
  { k: "status", label: "Status", options: ["active", "maintenance", "out_of_service", "retired"] },
];
const BLANK = { unit: "", plate: "", vin: "", year: "", make: "", model: "", vclass: "", trailer: "", reg_expiry: "", insurance_expiry: "", inspection_due: "", status: "active" };
export const VTONE = { active: "ok", maintenance: "warn", out_of_service: "bad", retired: "mute" };

export default function Vehicles({ data, reload }) {
  const [editing, setEditing] = useState(null);
  const [openId, setOpenId] = useState("");
  const save = async (form) => {
    if (editing.id) await foApi.updateVehicle(editing.id, form); else await foApi.addVehicle(form);
    setEditing(null);
    await reload();
  };
  const open = data.vehicles.find((v) => v.id === openId);
  return (
    <>
      <PageHead eyebrow="Fleet" title="Vehicles" sub="Units, plates and paperwork. Plates power automatic gate check-in; expiries feed your alerts.">
        <Button variant="primary" testid="fo-vehicle-add" onClick={() => setEditing({ ...BLANK })}><Plus className="h-4 w-4" />Add vehicle</Button>
      </PageHead>
      {editing && <RecordForm key={editing.id || "new"} prefix="fo-vehicle" fields={VEHICLE_FIELDS} initial={editing} onSave={save} onCancel={() => setEditing(null)} />}
      {data.vehicles.length ? (
        <Panel className="fo-rise fo-rise-1" testid="fo-vehicles-panel" title={`${data.vehicles.length} vehicles`}>
          <DataTable head={["Unit", "Plate", "Vehicle", "Registration", "Insurance", "Inspection", "Status", ""]} testid="fo-vehicles-table">
            {data.vehicles.map((v) => (
              <tr key={v.id} data-testid={`fo-vehicle-row-${v.id}`} className="cursor-pointer" onClick={() => setOpenId(v.id)}>
                <Td strong>{v.unit}{v.trailer ? <span className="block text-xs font-normal text-slate-400">{v.trailer}</span> : null}</Td>
                <Td mono>{v.plate ? <span className="tracking-wider text-[#0A234A]">{v.plate}</span> : <Tag tone="warn">no plate</Tag>}</Td>
                <Td>{[v.year, v.make, v.model].filter(Boolean).join(" ") || "—"}</Td>
                <Td><ExpiryCell date={v.reg_expiry} /></Td>
                <Td><ExpiryCell date={v.insurance_expiry} /></Td>
                <Td><ExpiryCell date={v.inspection_due} warn={14} /></Td>
                <Td><Tag tone={VTONE[v.status]}>{String(v.status).replace(/_/g, " ")}</Tag></Td>
                <Td className="text-right"><Button variant="ghost" testid={`fo-vehicle-open-${v.id}`} onClick={(e) => { e.stopPropagation(); setOpenId(v.id); }}>Details</Button></Td>
              </tr>
            ))}
          </DataTable>
        </Panel>
      ) : !editing && <EmptyState icon={Truck} testid="fo-vehicles-empty" title="No vehicles yet" body="Add your first unit to book parking and get gate proof."><Button variant="primary" testid="fo-vehicle-add-empty" onClick={() => setEditing({ ...BLANK })}>Add vehicle</Button></EmptyState>}
      <VehicleDrawer vehicle={open} data={data} reload={reload} onClose={() => setOpenId("")} onEdit={(v) => { setOpenId(""); setEditing({ ...BLANK, ...v, year: v.year ?? "" }); }} />
    </>
  );
}
